import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import JSZip from "jszip";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window.location.port === "3002"
    ? "http://localhost:5001"
    : "");

function Settings() {
  const [activeTab, setActiveTab] = useState("topics");
  const [topics, setTopics] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [editingAgeGroupId, setEditingAgeGroupId] = useState(null);
  const [editingTopicDescription, setEditingTopicDescription] = useState("");
  const [editingAgeGroupDescription, setEditingAgeGroupDescription] =
    useState("");
  const [showDeleteTopicModal, setShowDeleteTopicModal] = useState(false);
  const [showDeleteAgeGroupModal, setShowDeleteAgeGroupModal] = useState(false);
  const [showNewTopicModal, setShowNewTopicModal] = useState(false);
  const [showNewAgeGroupModal, setShowNewAgeGroupModal] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState(null);
  const [ageGroupToDelete, setAgeGroupToDelete] = useState(null);
  const [newTopicDescription, setNewTopicDescription] = useState("");
  const [newAgeGroupDescription, setNewAgeGroupDescription] = useState("");
  const [backupLoading, setBackupLoading] = useState(false);
  const [zohoSyncLoading, setZohoSyncLoading] = useState(false);
  const [zohoSyncSummary, setZohoSyncSummary] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch topics
      const topicsRef = collection(db, "units");
      const topicsQuery = query(topicsRef, orderBy("description"));
      const topicsSnapshot = await getDocs(topicsQuery);
      const topicsList = topicsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTopics(topicsList);

      // Fetch age groups
      const ageGroupsRef = collection(db, "age_groups");
      const ageGroupsQuery = query(ageGroupsRef, orderBy("description"));
      const ageGroupsSnapshot = await getDocs(ageGroupsQuery);
      const ageGroupsList = ageGroupsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAgeGroups(ageGroupsList);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error loading settings data");
      setLoading(false);
    }
  };

  // Topic management functions
  const handleNewTopic = () => {
    setNewTopicDescription("");
    setShowNewTopicModal(true);
  };

  const handleSaveTopic = async () => {
    if (!newTopicDescription.trim()) {
      toast.error("Please enter a description");
      return;
    }

    try {
      const topicsRef = collection(db, "units");
      await addDoc(topicsRef, {
        description: newTopicDescription.trim(),
        created_time: new Date(),
      });

      toast.success("Topic added successfully!");
      setNewTopicDescription("");
      setShowNewTopicModal(false);
      fetchData();
    } catch (error) {
      console.error("Error adding topic:", error);
      toast.error("Error adding topic");
    }
  };

  const handleCancelTopic = () => {
    setNewTopicDescription("");
    setShowNewTopicModal(false);
  };

  const handleEditTopic = (topic) => {
    setEditingTopicId(topic.id);
    setEditingTopicDescription(topic.description);
  };

  const handleSaveEditTopic = async () => {
    if (!editingTopicDescription.trim()) {
      toast.error("Please enter a description");
      return;
    }

    try {
      const topicRef = doc(db, "units", editingTopicId);
      await updateDoc(topicRef, {
        description: editingTopicDescription.trim(),
      });

      toast.success("Topic updated successfully!");
      setEditingTopicId(null);
      setEditingTopicDescription("");
      fetchData();
    } catch (error) {
      console.error("Error updating topic:", error);
      toast.error("Error updating topic");
    }
  };

  const handleCancelEditTopic = () => {
    setEditingTopicId(null);
    setEditingTopicDescription("");
  };

  const handleDeleteTopic = (topic) => {
    setTopicToDelete(topic);
    setShowDeleteTopicModal(true);
  };

  const confirmDeleteTopic = async () => {
    try {
      const topicRef = doc(db, "units", topicToDelete.id);
      await deleteDoc(topicRef);

      toast.success("Topic deleted successfully!");
      setShowDeleteTopicModal(false);
      setTopicToDelete(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting topic:", error);
      toast.error("Error deleting topic");
    }
  };

  const cancelDeleteTopic = () => {
    setShowDeleteTopicModal(false);
    setTopicToDelete(null);
  };

  // Age Group management functions
  const handleNewAgeGroup = () => {
    setNewAgeGroupDescription("");
    setShowNewAgeGroupModal(true);
  };

  const handleSaveAgeGroup = async () => {
    if (!newAgeGroupDescription.trim()) {
      toast.error("Please enter a description");
      return;
    }

    try {
      const ageGroupsRef = collection(db, "age_groups");
      await addDoc(ageGroupsRef, {
        description: newAgeGroupDescription.trim(),
        created_time: new Date(),
      });

      toast.success("Age group added successfully!");
      setNewAgeGroupDescription("");
      setShowNewAgeGroupModal(false);
      fetchData();
    } catch (error) {
      console.error("Error adding age group:", error);
      toast.error("Error adding age group");
    }
  };

  const handleCancelAgeGroup = () => {
    setNewAgeGroupDescription("");
    setShowNewAgeGroupModal(false);
  };

  const handleEditAgeGroup = (ageGroup) => {
    setEditingAgeGroupId(ageGroup.id);
    setEditingAgeGroupDescription(ageGroup.description);
  };

  const handleSaveEditAgeGroup = async () => {
    if (!editingAgeGroupDescription.trim()) {
      toast.error("Please enter a description");
      return;
    }

    try {
      const ageGroupRef = doc(db, "age_groups", editingAgeGroupId);
      await updateDoc(ageGroupRef, {
        description: editingAgeGroupDescription.trim(),
      });

      toast.success("Age group updated successfully!");
      setEditingAgeGroupId(null);
      setEditingAgeGroupDescription("");
      fetchData();
    } catch (error) {
      console.error("Error updating age group:", error);
      toast.error("Error updating age group");
    }
  };

  const handleCancelEditAgeGroup = () => {
    setEditingAgeGroupId(null);
    setEditingAgeGroupDescription("");
  };

  const handleDeleteAgeGroup = (ageGroup) => {
    setAgeGroupToDelete(ageGroup);
    setShowDeleteAgeGroupModal(true);
  };

  const confirmDeleteAgeGroup = async () => {
    try {
      const ageGroupRef = doc(db, "age_groups", ageGroupToDelete.id);
      await deleteDoc(ageGroupRef);

      toast.success("Age group deleted successfully!");
      setShowDeleteAgeGroupModal(false);
      setAgeGroupToDelete(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting age group:", error);
      toast.error("Error deleting age group");
    }
  };

  const cancelDeleteAgeGroup = () => {
    setShowDeleteAgeGroupModal(false);
    setAgeGroupToDelete(null);
  };

  // Serialize Firestore data for JSON export (handles Timestamps, DocumentReferences)
  const serializeForExport = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj.toDate === "function") {
      return obj.toDate().toISOString();
    }
    if (typeof obj?.path === "string" && obj?.firestore) {
      return obj.path;
    }
    if (Array.isArray(obj)) {
      return obj.map(serializeForExport);
    }
    if (typeof obj === "object") {
      const result = {};
      for (const [key, value] of Object.entries(obj)) {
        result[key] = serializeForExport(value);
      }
      return result;
    }
    return obj;
  };

  const handleSyncAdminsToZoho = async () => {
    const user = auth.currentUser;
    if (!user) {
      toast.error("You must be signed in.");
      return;
    }
    setZohoSyncLoading(true);
    setZohoSyncSummary(null);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_BASE}/api/zoho/sync-admins`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Sync failed (${res.status})`);
      }
      setZohoSyncSummary(data);

      const c = data.counts || {};
      const pushed =
        c.pushed ??
        (Array.isArray(data.ok) ? data.ok.length : 0);
      const skipped =
        c.skipped ??
        (Array.isArray(data.skipped) ? data.skipped.length : 0);
      const failed =
        c.failed ??
        (Array.isArray(data.errors) ? data.errors.length : 0);
      const created = c.created ?? 0;
      const updated = c.updated ?? 0;

      if (failed > 0) {
        toast.error(
          `Zoho CRM: ${pushed} admin(s) pushed, ${failed} failed, ${skipped} skipped. See details below.`,
          { duration: 8000 },
        );
      } else if (pushed === 0 && skipped === 0) {
        toast.success(
          "Zoho CRM: no library admins found to sync (user_type = admin with org + HRF ID).",
          { duration: 6000 },
        );
      } else {
        const parts = [`${pushed} admin(s) pushed to CRM`];
        if (created || updated) {
          parts.push(
            `(${created} created, ${updated} updated)`,
          );
        }
        if (skipped > 0) {
          parts.push(`${skipped} skipped`);
        }
        toast.success(`Zoho CRM: ${parts.join(" — ")}.`, { duration: 7000 });
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Zoho sync failed", { duration: 8000 });
    } finally {
      setZohoSyncLoading(false);
    }
  };

  const handleDownloadBackup = async () => {
    const collections = [
      "videos",
      "node_lessons",
      "node_lesson_tasks",
      "organizations",
      "users",
      "units",
      "age_groups",
    ];

    try {
      setBackupLoading(true);
      const zip = new JSZip();

      for (const collectionName of collections) {
        const snapshot = await getDocs(collection(db, collectionName));
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...serializeForExport(doc.data()),
        }));
        const json = JSON.stringify(data, null, 2);
        zip.file(`${collectionName}.json`, json);
      }

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kuzko-backup-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success("Backup downloaded successfully!");
    } catch (error) {
      console.error("Error creating backup:", error);
      toast.error("Failed to create backup");
    } finally {
      setBackupLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading settings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary-600">Settings</h1>
            <p className="text-gray-600 mt-2">Manage topics and age groups</p>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab("topics")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "topics"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Topics
                </button>
                <button
                  onClick={() => setActiveTab("ageGroups")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "ageGroups"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Age Groups
                </button>
                <button
                  onClick={() => setActiveTab("backup")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "backup"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Backup
                </button>
                <button
                  onClick={() => setActiveTab("zoho")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "zoho"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Zoho CRM
                </button>
              </nav>
            </div>
          </div>

          {/* Topics Tab */}
          {activeTab === "topics" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Topics</h2>
                <button
                  onClick={handleNewTopic}
                  className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors flex items-center space-x-2"
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>New Topic</span>
                </button>
              </div>

              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Existing Topics */}
                    {topics.map((topic) => (
                      <tr key={topic.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingTopicId === topic.id ? (
                            <input
                              type="text"
                              value={editingTopicDescription}
                              onChange={(e) =>
                                setEditingTopicDescription(e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              autoFocus
                            />
                          ) : (
                            <div className="text-sm text-gray-900">
                              {topic.description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {editingTopicId === topic.id ? (
                              <>
                                <button
                                  onClick={handleSaveEditTopic}
                                  className="text-green-600 hover:text-green-900 transition-colors p-1"
                                  title="Save"
                                >
                                  <CheckIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={handleCancelEditTopic}
                                  className="text-red-600 hover:text-red-900 transition-colors p-1"
                                  title="Cancel"
                                >
                                  <XMarkIcon className="w-5 h-5" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditTopic(topic)}
                                  className="text-blue-600 hover:text-blue-900 transition-colors p-1"
                                  title="Edit"
                                >
                                  <PencilIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteTopic(topic)}
                                  className="text-red-600 hover:text-red-900 transition-colors p-1"
                                  title="Delete"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Zoho CRM Tab */}
          {activeTab === "zoho" && (
            <div>
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Zoho CRM — admin contacts
                </h2>
                <p className="text-sm text-gray-500 mt-2 max-w-2xl">
                  Push <strong>library admins</strong> only (users with{" "}
                  <code className="text-xs bg-gray-100 px-1 rounded">user_type</code>{" "}
                  = admin)—not super admins—to Zoho as <strong>Contacts</strong>:{" "}
                  <strong>Email</strong> plus <strong>Account Name</strong> (lookup)
                  from the Account whose <strong>HRF_ID</strong> matches each
                  organization&apos;s <strong>HRF ID</strong> in{" "}
                  <span className="whitespace-nowrap">Organizations → Edit</span>.
                </p>
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-3 max-w-2xl">
                  One-time setup: complete OAuth at{" "}
                  <code className="text-xs bg-amber-100 px-1 rounded">
                    /zoho-oauthgrant
                  </code>
                  , then paste <strong>refresh token</strong> and{" "}
                  <strong>api domain</strong> into server{" "}
                  <code className="text-xs bg-amber-100 px-1 rounded">
                    ZOHO_OAUTH_CONFIG
                  </code>{" "}
                  (<code className="text-xs">refreshToken</code>,{" "}
                  <code className="text-xs">apiDomain</code>).
                </p>
              </div>
              <button
                type="button"
                onClick={handleSyncAdminsToZoho}
                disabled={zohoSyncLoading}
                className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {zohoSyncLoading ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span>Syncing to Zoho…</span>
                  </>
                ) : (
                  <span>Sync admin users to Zoho CRM</span>
                )}
              </button>

              {zohoSyncSummary && (
                <div className="mt-6 max-w-3xl space-y-4 text-sm">
                  {zohoSyncSummary.counts && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-800">
                      <p className="font-medium text-gray-900">Last run</p>
                      <ul className="mt-2 grid gap-1 sm:grid-cols-2 text-sm">
                        <li>
                          <span className="text-green-700 font-semibold">
                            {zohoSyncSummary.counts.pushed}
                          </span>{" "}
                          pushed
                          {(zohoSyncSummary.counts.created > 0 ||
                            zohoSyncSummary.counts.updated > 0) && (
                            <span className="text-gray-600">
                              {" "}
                              ({zohoSyncSummary.counts.created} created,{" "}
                              {zohoSyncSummary.counts.updated} updated)
                            </span>
                          )}
                        </li>
                        <li>
                          <span className="text-gray-700 font-semibold">
                            {zohoSyncSummary.counts.skipped}
                          </span>{" "}
                          skipped
                        </li>
                        <li>
                          <span
                            className={
                              zohoSyncSummary.counts.failed > 0
                                ? "text-red-700 font-semibold"
                                : "text-gray-700 font-semibold"
                            }
                          >
                            {zohoSyncSummary.counts.failed}
                          </span>{" "}
                          failed
                        </li>
                      </ul>
                    </div>
                  )}
                  {zohoSyncSummary.meta && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50/90 px-4 py-3 text-gray-800">
                      <p className="font-medium text-gray-900">Zoho API target</p>
                      <p className="mt-1 text-xs break-all font-mono text-gray-700">
                        {zohoSyncSummary.meta.apiDomain}
                      </p>
                      {zohoSyncSummary.meta.environment && (
                        <p className="mt-1 text-xs text-gray-600">
                          Environment:{" "}
                          <span className="font-semibold">
                            {zohoSyncSummary.meta.environment}
                          </span>
                        </p>
                      )}
                      {zohoSyncSummary.meta.hint && (
                        <p className="mt-2 text-xs text-amber-900 leading-snug">
                          {zohoSyncSummary.meta.hint}
                        </p>
                      )}
                    </div>
                  )}
                  {zohoSyncSummary.ok && zohoSyncSummary.ok.length > 0 && (
                    <div>
                      <h3 className="font-medium text-green-800 mb-1">
                        Synced ({zohoSyncSummary.ok.length})
                      </h3>
                      <ul className="list-disc pl-5 text-gray-700 space-y-0.5 max-h-40 overflow-y-auto">
                        {zohoSyncSummary.ok.map((row, i) => (
                          <li key={`ok-${i}`}>
                            {row.email} — {row.action}
                            {row.id ? ` (id ${row.id})` : ""}
                            {row.zohoMessage ? (
                              <span className="text-gray-600">
                                {" "}
                                — {row.zohoMessage}
                                {row.zohoCode ? ` [${row.zohoCode}]` : ""}
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {zohoSyncSummary.skipped &&
                    zohoSyncSummary.skipped.length > 0 && (
                      <div>
                        <h3 className="font-medium text-gray-800 mb-1">
                          Skipped ({zohoSyncSummary.skipped.length})
                        </h3>
                        <ul className="list-disc pl-5 text-gray-600 space-y-0.5 max-h-40 overflow-y-auto text-xs">
                          {zohoSyncSummary.skipped.map((row, i) => (
                            <li key={`skip-${i}`}>
                              {row.email || row.uid}: {row.reason}
                              {row.hrefId != null && row.hrefId !== "" && (
                                <span className="text-gray-500">
                                  {" "}
                                  (hrefId tried: {String(row.hrefId)})
                                </span>
                              )}
                              {row.hint && (
                                <div className="mt-0.5 text-gray-500 italic">
                                  {row.hint}
                                </div>
                              )}
                              {row.accountDiscovery && (
                                <pre className="mt-2 max-h-64 overflow-auto rounded border border-amber-200 bg-amber-50/80 p-2 text-[11px] text-gray-800 whitespace-pre-wrap break-all">
                                  {JSON.stringify(row.accountDiscovery, null, 2)}
                                </pre>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  {zohoSyncSummary.errors &&
                    zohoSyncSummary.errors.length > 0 && (
                      <div>
                        <h3 className="font-medium text-red-800 mb-1">
                          Errors ({zohoSyncSummary.errors.length})
                        </h3>
                        <ul className="list-disc pl-5 text-red-700 space-y-0.5 max-h-40 overflow-y-auto text-xs">
                          {zohoSyncSummary.errors.map((row, i) => (
                            <li key={`err-${i}`}>
                              {row.email || row.uid}: {row.error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                </div>
              )}
            </div>
          )}

          {/* Backup Tab */}
          {activeTab === "backup" && (
            <div>
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Data Backup
                </h2>
                <p className="text-sm text-gray-500 mt-2">
                  Download a backup of all your data as a ZIP file. Includes:
                  videos, lessons, lesson tasks, organizations, users, units, and
                  age groups.
                </p>
              </div>
              <button
                onClick={handleDownloadBackup}
                disabled={backupLoading}
                className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {backupLoading ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Creating backup...</span>
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="w-5 h-5" />
                    <span>Download Backup</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Age Groups Tab */}
          {activeTab === "ageGroups" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Age Groups
                </h2>
                <button
                  onClick={handleNewAgeGroup}
                  className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors flex items-center space-x-2"
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>New Age Group</span>
                </button>
              </div>

              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Existing Age Groups */}
                    {ageGroups.map((ageGroup) => (
                      <tr key={ageGroup.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingAgeGroupId === ageGroup.id ? (
                            <input
                              type="text"
                              value={editingAgeGroupDescription}
                              onChange={(e) =>
                                setEditingAgeGroupDescription(e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              autoFocus
                            />
                          ) : (
                            <div className="text-sm text-gray-900">
                              {ageGroup.description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {editingAgeGroupId === ageGroup.id ? (
                              <>
                                <button
                                  onClick={handleSaveEditAgeGroup}
                                  className="text-green-600 hover:text-green-900 transition-colors p-1"
                                  title="Save"
                                >
                                  <CheckIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={handleCancelEditAgeGroup}
                                  className="text-red-600 hover:text-red-900 transition-colors p-1"
                                  title="Cancel"
                                >
                                  <XMarkIcon className="w-5 h-5" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditAgeGroup(ageGroup)}
                                  className="text-blue-600 hover:text-blue-900 transition-colors p-1"
                                  title="Edit"
                                >
                                  <PencilIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteAgeGroup(ageGroup)}
                                  className="text-red-600 hover:text-red-900 transition-colors p-1"
                                  title="Delete"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* New Topic Modal */}
          {showNewTopicModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Add New Topic
                  </h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={newTopicDescription}
                      onChange={(e) => setNewTopicDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter topic description"
                      autoFocus
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={handleCancelTopic}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveTopic}
                      className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* New Age Group Modal */}
          {showNewAgeGroupModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Add New Age Group
                  </h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={newAgeGroupDescription}
                      onChange={(e) =>
                        setNewAgeGroupDescription(e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter age group description"
                      autoFocus
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={handleCancelAgeGroup}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveAgeGroup}
                      className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Topic Modal */}
          {showDeleteTopicModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3 text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Delete Topic
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    Are you sure you want to delete "
                    {topicToDelete?.description}"? This action cannot be undone.
                  </p>
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={confirmDeleteTopic}
                      className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                    <button
                      onClick={cancelDeleteTopic}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Age Group Modal */}
          {showDeleteAgeGroupModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3 text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Delete Age Group
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    Are you sure you want to delete "
                    {ageGroupToDelete?.description}"? This action cannot be
                    undone.
                  </p>
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={confirmDeleteAgeGroup}
                      className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                    <button
                      onClick={cancelDeleteAgeGroup}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;
